import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { 
  doc, 
  collection, 
  query, 
  where, 
  onSnapshot, 
  updateDoc, 
  writeBatch,
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';

import CariDuzenleModal from '../components/CariDuzenleModal'; 

// --- PDF/EXCEL KÜTÜPHANELERİ ---
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; 
import * as XLSX from 'xlsx'; 

// --- ONAY MODALI ---
const ConfirmationModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-backdrop">
            <div className="modal-content">
                <h3 className="modal-title">{title}</h3>
                <p className="modal-body">{message}</p>
                <div className="modal-footer">
                    <button className="admin-button-secondary" onClick={onCancel}>Vazgeç</button>
                    <button className="admin-button" onClick={onConfirm}>Onayla</button>
                </div>
            </div>
        </div>
    );
};

function formatDate(timestamp) {
  if (!timestamp) return '---';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('tr-TR');
}

function formatCurrency(number) {
    if (typeof number !== 'number') return '0,00 ₺';
    return number.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
}
function formatCurrencyRaw(number) {
    if (typeof number !== 'number') return 0;
    return number; 
}

// --- ANA COMPONENT ---
function MusteriDetay() {
  const { id: cariId } = useParams(); 
  const navigate = useNavigate(); 

  const [cari, setCari] = useState(null);
  const [taksitler, setTaksitler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [isDuzenleModalOpen, setIsDuzenleModalOpen] = useState(false); 
  
  const [isModalOpen, setIsModalOpen] = useState(false); 
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Tarih/Saat için state (Modern Header için)
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // --- Veri Çekme ---
  useEffect(() => {
    setLoading(true);
    setError('');
    if (!cariId) { setError("ID bulunamadı."); setLoading(false); return; }

    const cariRef = doc(db, 'cariler', cariId);
    const unsubscribeCari = onSnapshot(cariRef, (docSnap) => {
        if (docSnap.exists()) { setCari({ id: docSnap.id, ...docSnap.data() }); } 
        else { setError('Müşteri bulunamadı.'); }
    }, (err) => setError('Hata: ' + err.message));

    const taksitlerQuery = query(collection(db, 'taksitler'), where('cariId', '==', cariId));
    const unsubscribeTaksitler = onSnapshot(taksitlerQuery, (snapshot) => {
        const taksitlerList = snapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data(),
            odemeGunu: doc.data().odemeGunu,
        })).sort((a, b) => (a.odemeGunu?.toDate() || 0) - (b.odemeGunu?.toDate() || 0)); 
        setTaksitler(taksitlerList);
        setLoading(false);
    });

    return () => { unsubscribeCari(); unsubscribeTaksitler(); };
  }, [cariId]);

  const { toplamBorc, odenenBorc, kalanBorc, vadesiGecenMiktar } = useMemo(() => {
    const tpb = taksitler.reduce((acc, t) => acc + (t.miktar || 0), 0);
    const ot = taksitler.filter(t => t.durum === 'Ödendi').reduce((acc, t) => acc + (t.miktar || 0), 0);
    const ka = tpb - ot;
    const vgm = taksitler.filter(t => t.durum === 'Bekliyor' && (t.odemeGunu?.toDate() || new Date()) < new Date()).reduce((acc, t) => acc + (t.miktar || 0), 0);
    return { toplamBorc: tpb, odenenBorc: ot, kalanBorc: ka, vadesiGecenMiktar: vgm };
  }, [taksitler]);

  const openModal = useCallback((type, taksit) => {
    let title = "";
    let message = "";
    let onConfirm = () => {};
    
    if (type === 'pay') {
        title = "Ödeme Onayı";
        message = `${formatCurrency(taksit.miktar)} tutarındaki taksiti 'Ödendi' olarak işaretlemek istediğinizden emin misiniz?`;
        onConfirm = () => handleTaksitUpdate(taksit.id, 'Ödendi');
    } else if (type === 'delete') {
        title = "Silme Onayı";
        message = "Bu taksiti silmek istediğinizden emin misiniz?";
        onConfirm = () => handleTaksitDelete(taksit.id);
    } else if (type === 'topluPay') {
        const bekleyenler = taksitler.filter(t => t.durum === 'Bekliyor').length;
        if (bekleyenler === 0) { alert("Ödenmemiş taksit yok."); return; }
        title = "Toplu Ödeme";
        message = `${bekleyenler} adet taksitin tamamını 'Ödendi' yapmak istiyor musunuz?`;
        onConfirm = handleTopluOdeme;
    }
    setModalData({ title, message, onConfirm });
    setIsModalOpen(true);
  }, [taksitler]); 

  const handleTopluOdeme = async () => {
    setIsModalOpen(false); setModalLoading(true);
    try {
        const batch = writeBatch(db);
        taksitler.filter(t => t.durum === 'Bekliyor').forEach(taksit => {
            const taksitRef = doc(db, 'taksitler', taksit.id);
            batch.update(taksitRef, { durum: "Ödendi", odemeTarihi: serverTimestamp() });
        });
        await batch.commit();
    } catch (err) { setError("Toplu ödeme başarısız."); }
    setModalLoading(false);
  };

  const handleTaksitUpdate = async (taksitId, newDurum) => {
    setIsModalOpen(false);
    try {
        await updateDoc(doc(db, 'taksitler', taksitId), { 
            durum: newDurum, odemeTarihi: newDurum === 'Ödendi' ? serverTimestamp() : null
        });
    } catch (err) { setError("Güncelleme hatası."); }
  };

  const handleTaksitDelete = async (taksitId) => {
    setIsModalOpen(false); 
    try { await deleteDoc(doc(db, 'taksitler', taksitId)); } catch (err) { setError("Silme hatası."); }
  };
  
  const handlePdfExport = () => {
    if (!cari) return; 
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(`Taksit Raporu: ${cari.adSoyad || cari.firmaSahis}`, 14, 22);
    autoTable(doc, {
        head: [["Sıra No", "Ödeme Günü", "Miktar", "Durum"]], 
        body: taksitler.map((t, i) => [i + 1, formatDate(t.odemeGunu), formatCurrency(t.miktar), t.durum.toUpperCase()]),
        startY: 50
    });
    doc.save(`${cari.adSoyad || cari.firmaSahis}-taksitler.pdf`);
  };

  const handleExcelExport = () => {
    if (!cari) return; 
    const headers = ['Sıra No', 'Müşteri', 'Telefon', 'Ödeme Günü', 'Miktar (TL)', 'Durum'];
    const data = taksitler.map((t, index) => [
      index + 1, cari.adSoyad || cari.firmaSahis, cari.telefon, formatDate(t.odemeGunu), formatCurrencyRaw(t.miktar), t.durum.toUpperCase()
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Taksitler');
    XLSX.writeFile(wb, `${cari.adSoyad || cari.firmaSahis}-taksitler.xlsx`);
  };

  if (loading) return <p className="loading-text">Yükleniyor...</p>;
  if (error) return <p className="login-error">{error}</p>;
  if (!cari) return <p className="login-error">Müşteri bulunamadı.</p>;

  return (
    <>
      <div className="admin-page">
        {/* --- MODERN & LOGOLU HEADER --- */}
        <div className="admin-header">
          {/* Tıklanabilir Logo/Başlık */}
          <h1 
              className="brand-logo admin-brand-header"
              onClick={() => navigate('/admin/dashboard')}
              style={{ cursor: 'pointer', flex: 1 }}
              title="Admin Paneline Dön"
          >
            <img src="/logo.png" width="50" alt="" />
            ARAS ASAF İNŞAAT – MÜŞTERİ DETAYI
          </h1>

          {/* Sağ Taraf: Tarih ve Düzenle Butonu */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              
              <div className="date-time-info" style={{marginRight: '10px'}}>
                {now.toLocaleDateString("tr-TR")} | {now.toLocaleTimeString("tr-TR")}
              </div>

              <button 
                  onClick={() => setIsDuzenleModalOpen(true)} 
                  className="admin-button"
                  style={{ padding: '10px 20px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                  ✏️ Düzenle
              </button>

               <button 
                  onClick={() => navigate(-1)} 
                  className="admin-button-secondary small"
              >
                  ← Geri
              </button>
          </div>
        </div>

        {/* --- İÇERİK --- */}
        <div className="form-section">
          <div className="customer-detail-grid">
            <div className="customer-info">
              <h2>{cari.adSoyad || cari.firmaSahis}</h2>
              <div className="info-item"><strong>TC/Vergi:</strong> {cari.tc || '---'}</div>
              <div className="info-item"><strong>Telefon:</strong> {cari.telefon}</div>
              <div className="info-item"><strong>Adres:</strong> {cari.adres || '---'}</div>
            </div>
            
            <div className="customer-summary">
              <div className="summary-box"><span>Toplam Borç</span><p>{formatCurrency(toplamBorc)}</p></div>
              <div className="summary-box paid"><span>Ödenen</span><p>{formatCurrency(odenenBorc)}</p></div>
              <div className="summary-box pending"><span>Kalan Borç</span><p>{formatCurrency(kalanBorc)}</p></div>
               <div className="summary-box gider">
                <span>Vadesi Geçen</span>
                <p style={{ color: vadesiGecenMiktar > 0 ? '#dc3545' : '#28a745' }}>{formatCurrency(vadesiGecenMiktar)}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="admin-table-container">
          <div className="taksit-controls">
            <h2>Ödeme Planı</h2>
            <div>
              <button onClick={() => openModal('topluPay')} className="admin-button-secondary small">Hepsini Öde</button>
              <button onClick={handlePdfExport} className="admin-button-secondary small">PDF Aktar</button>
              <button onClick={handleExcelExport} className="admin-button-secondary small">Excel Aktar</button>
            </div>
          </div>
          
          <table className="admin-table">
            <thead>
              <tr>
                <th>Miktar</th>
                <th>Ödeme Günü</th>
                <th>Durum</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {taksitler.length === 0 ? (
                <tr><td colSpan="4" className="no-data">Bu cariye ait taksit bulunmamaktadır.</td></tr>
              ) : (
                taksitler.map((taksit) => {
                    const isOverdue = taksit.durum === 'Bekliyor' && taksit.odemeGunu.toDate() < new Date();
                    const statusClass = taksit.durum === 'Ödendi' ? 'status-paid' : (isOverdue ? 'status-overdue' : 'status-pending');

                    return (
                        <tr key={taksit.id}>
                            <td>{formatCurrency(taksit.miktar)}</td>
                            <td>{formatDate(taksit.odemeGunu)}</td>
                            <td><span className={statusClass}>{isOverdue ? 'VADESİ GEÇTİ' : taksit.durum.toUpperCase()}</span></td>
                            <td>
                                {taksit.durum === 'Bekliyor' ? (
                                    <button onClick={() => openModal('pay', taksit)} className="admin-button-odeme small">Ödendi Yap</button>
                                ) : (
                                    <button onClick={() => openModal('delete', taksit)} className="admin-button-delete small">Sil</button>
                                )}
                                <button onClick={() => openModal('delete', taksit)} className="admin-button-delete small" style={{marginLeft: '5px'}}>Sil</button>
                            </td>
                        </tr>
                    );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmationModal 
          isOpen={isModalOpen}
          title={modalData?.title}
          message={modalData?.message}
          onConfirm={modalData?.onConfirm}
          onCancel={() => setIsModalOpen(false)}
      />
      
      {/* YENİ GENİŞ MODAL */}
      <CariDuzenleModal
        show={isDuzenleModalOpen}
        onClose={() => setIsDuzenleModalOpen(false)}
        cariData={cari}
        taksitData={taksitler}
      />
    </>
  );
}

export default MusteriDetay;